import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatCents, formatDollars } from './_money';
import {
  X, Loader2, ShieldAlert, AlertTriangle, ExternalLink,
  ChevronDown, ChevronUp, ArrowUp, ArrowDown, Search, ChevronsUpDown,
} from 'lucide-react';

/*
 * MetricDrilldown — the rows behind a CEO-dashboard figure (card F1, 2026-08-25).
 *
 * Calls analytics:drilldown:resolve and renders ONLY the allowlisted row shape.
 * Every PHI decision (permission, intent audit, subject-set audit, row cap) is
 * made server-side in routes/_analytics_drilldown.js; this component displays a
 * result and must not re-derive anything from it.
 *
 * ── NAMES (Daniel's ruling 2026-08-25) ────────────────────────────────────
 * Rows are now NAME-BEARING. This supersedes the 2026-08-23 no-identifying-text
 * ruling for that one field: #393's subject-set audit records exactly which
 * contacts each resolve disclosed, so showing the name changes what an
 * AUTHORIZED VIEWER SEES, not what is disclosed. Everything else on the forbidden
 * list stays off the wire and off this table.
 *
 * ── THREE HONESTY RULES, each guarding a failure this repo has shipped ─────
 *  1. AN EMPTY RESULT SAYS SO. A blank panel is indistinguishable from a broken
 *     one, and the server returns a structured error for every failure precisely
 *     so that empty `rows` means "no rows matched" and nothing else.
 *     ⚠ Search adds a SECOND empty state, and they must not be confused: "no rows
 *     behind this figure" and "your search matched none of them" are different
 *     facts about the world.
 *  2. TRUNCATION IS DISCLOSED, with its number. Rendering a capped list as though
 *     complete is the pagination silent-cap incident — where the FIX for a silent
 *     failure contained the same silent failure.
 *  3. THE HORIZON WARNING IS INLINE on the result (design §7) — "not in a
 *     footnote, not in a tooltip, not in documentation".
 *
 * ⚠ SORT AND SEARCH ARE PURELY CLIENT-SIDE, over the already-returned capped set.
 * No new query, no new disclosure, and NOTHING to re-audit — the resolve that
 * fetched these rows already recorded the full subject set. Sorting or filtering
 * a list you have already been shown discloses nothing further. If either ever
 * needs to reach the server, it becomes a new disclosure and needs its own audit.
 *
 * ⚠ Row → contact pushes a FRAME on the drill-down stack (Card P167/P179); it
 * does NOT fetch here and it does NOT use the old sessionStorage hand-off.
 * The record mounts inside the modal and CARRIES THE PHI AUDIT itself.
 *
 * ⛔ THIS COMMENT SAID "the established sessionStorage hand-off" UNTIL CARD
 * P186. That stopped being true when the frame stack landed, and a stale
 * comment naming a retired mechanism is how the next reader concludes the old
 * path is still live and goes looking for a bug in it. Corrected, not deleted,
 * so the change of mechanism is on the record.
 */

/*
 * The allowlisted shape (resolver's OPP_COLUMNS). This is a SECOND, INDEPENDENT
 * statement of the same allowlist, so a server-side widening cannot silently
 * surface a new column here — the two-surface trap that left #407 half-connected.
 *
 * `sortAs` tells the comparator how to read the cell:
 *   'lastName' — ⚠ Daniel named alphabetical-by-name explicitly, and "sensibly"
 *                means BY LAST NAME. Sorting the display string would order by
 *                first name, which is not how anyone looks for a person.
 *   'number'   — money/ids compare numerically ('9' < '10', not the string order).
 *   'date'     — epoch compare.
 */
const OPPORTUNITY_COLUMNS = Object.freeze([
  { key: 'name', label: 'Name', sortAs: 'lastName' },
  { key: 'id', label: 'ID', sortAs: 'number', mono: true },
  { key: 'value', label: 'Value', sortAs: 'number', money: true, align: 'right' },
  { key: 'stage', label: 'Stage', sortAs: 'text' },
  { key: 'created_at', label: 'Created', sortAs: 'date' },
  { key: 'owner', label: 'Owner', sortAs: 'text' },
  // P140 PR B — the column the summary groups by, rendered here so the list and
  // the summary above it describe rows the same way.
  { key: 'source', label: 'Source', sortAs: 'text' },
]);

/*
 * CRM-sourced money. ⚠ Matches CeoDashboard's `money()`, NOT its `moneyIn()`:
 * crm_opportunities has NO currency column, so "$" is the only symbol that can be
 * printed honestly. `moneyIn()` is the currency-aware Stripe formatter, and using
 * it here would print a currency this data does not carry.
 * `value` is NUMERIC(12,2) and arrives as a string.
 */
/*
 * ⛔ The local `money()` is gone. It took DOLLARS, which was correct for the
 * `value` column — but the same name took CENTS one component over, and that
 * collision is what shipped the 28 Oct bug. One formatter, and it names its unit.
 */

/** Last whitespace-separated token, for "sensible" alphabetical-by-person order. */
const lastNameKey = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts[parts.length - 1].toLowerCase() : '';
};

const cellText = (col, row) => {
  const v = row[col.key];
  if (v == null || v === '') return '—';
  if (col.money) return col.cents ? formatCents(v) : formatDollars(v);
  if (col.sortAs === 'date') { const d = new Date(v); return isNaN(d) ? String(v) : d.toLocaleDateString(); }
  return String(v);
};

const sortValue = (col, row) => {
  const v = row[col.key];
  switch (col.sortAs) {
    case 'lastName': return lastNameKey(v);
    case 'number': return Number(v == null ? Number.NEGATIVE_INFINITY : v);
    case 'date': { const t = new Date(v).getTime(); return isNaN(t) ? Number.NEGATIVE_INFINITY : t; }
    default: return String(v == null ? '' : v).toLowerCase();
  }
};

const COLLAPSE_KEY = (metricKey) => `zg.drilldown.collapsed.${metricKey}`;

import { saveCsv } from './_download';
import { ANALYTICS_CHANNELS } from './MetricSummary';
export default function MetricDrilldown({ metricKey, title, onClose, onOpenContact, group, onBack, canExport, channels = ANALYTICS_CHANNELS, columns, filters = null }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  // Default value-descending, per Daniel's ruling.
/*
 * The order the FIGURE was computed in, used whenever no column is actively
 * sorted. ⚠ Named rather than inlined so "off" and "initial" cannot drift —
 * they are the same state and must stay so.
 */
const DEFAULT_SORT = Object.freeze({ key: 'value', dir: 'desc' });
/* ⛔ `key: null` IS THE OFF STATE, and it is where the table STARTS. Modelling
 * the initial view as an active sort on `value` made the first click on Value a
 * no-op (desc → "off" → the same desc), which is worse than no cycle at all:
 * the header responds to a click by doing nothing visible. */
const SORT_OFF = Object.freeze({ key: null, dir: null });

  const [sort, setSort] = useState({ ...SORT_OFF });
  const [query, setQuery] = useState('');
  /*
   * ⭐ P185 item 3 — a date window WITHIN the dashboard's window.
   *
   * ⚠ It narrows the rows ALREADY FETCHED; it does not re-query. The dashboard
   * range decides which rows exist, this decides which of them you are looking
   * at — two different questions, and conflating them would make the table
   * disagree with the figure that opened it.
   *
   * ⛔ It applies to the table's OWN date column, whichever that is (created,
   * closed, issued, due), read from the column allowlist rather than assumed —
   * finance tables have no `created_at` and a hard-coded key would silently
   * filter nothing.
   */
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  /*
   * ⚠⚠ STILL AN ALLOWLIST, just a per-registry one (P150). The invariant that
   * matters is unchanged and is why this is a prop rather than a render of
   * whatever keys the row happens to carry: a field the server should never
   * have sent is NOT rendered even if it arrives. Finance rows have a different
   * shape from opportunity rows; neither may display a column nobody reviewed.
   */
  const COLUMNS = columns || OPPORTUNITY_COLUMNS;
  // P140 PR B — the last level is PAGED, 25 at a time, scoped to the group the
  // user picked in the summary.
  const [offset, setOffset] = useState(0);
  const [exportState, setExportState] = useState({ busy: false, error: null, done: null });
  // Reset to the first page whenever the group changes — otherwise opening a
  // small group from page 3 of a large one shows an empty list and reads as
  // "this group has nothing in it".
  useEffect(() => { setOffset(0); }, [metricKey, group && group.dimension, group && group.key]);
  // Collapsible PER TILE: the preference is keyed by metric, so collapsing the
  // pipeline panel does not collapse the opportunities one.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY(metricKey)) === '1'; } catch (e) { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY(metricKey), collapsed ? '1' : '0'); }
    catch (e) { /* private mode — the panel still works, it just will not remember */ }
  }, [metricKey, collapsed]);

  const load = useCallback(async () => {
    setState({ loading: true, error: null, data: null });
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.invoke !== 'function') throw new Error('Drill-down is unavailable in this context.');
      const res = await api.invoke(channels.resolve, {
        key: metricKey,
        group: group ? { dimension: group.dimension, key: group.key } : null,
        offset,
        filters,
      });
      // A refusal is NOT an empty result. Surfacing the server's message keeps
      // "you may not see this" distinct from "there is nothing to see".
      if (res && res.error) throw new Error(res.message || 'That figure could not be opened.');
      setState({ loading: false, error: null, data: res });
    } catch (e) {
      setState({ loading: false, error: e.message || 'That figure could not be opened.', data: null });
    }
  }, [metricKey, group && group.dimension, group && group.key, offset, channels, filters]);

  useEffect(() => { load(); }, [load]);

  /*
   * CSV export. ⚠ The button is only OFFERED when canExport is true, and that is
   * a convenience, not the control: the server re-checks
   * analytics:drilldown:export on the channel AND inside the resolver. A client
   * flag is not a permission.
   */
  const doExport = useCallback(async () => {
    setExportState({ busy: true, error: null, done: null });
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.invoke !== 'function') throw new Error('Export is unavailable in this context.');
      const res = await api.invoke(channels.export, {
        key: metricKey,
        group: group ? { dimension: group.dimension, key: group.key } : null,
        filters,
        /*
         * ⭐ THE SCREEN'S CURRENT SORT travels with the request, so the file
         * is ordered the way the person looking at it ordered the table. The
         * server re-checks it against its own allowlist; an unknown key falls
         * back to the metric's declared order rather than being interpolated.
         */
        sort: sort.key && sort.dir ? { key: sort.key, dir: sort.dir } : null,
      });
      if (res && res.error) throw new Error(res.message || 'That figure could not be exported.');

      /*
       * ⛔⛔ THE SAVE STEP. This is the whole of Carlos's "Export CSV doesn't
       * work": the server built the file, the audit fired, this component
       * rendered "Exported 25 rows as …" — and nothing was ever handed to the
       * browser. Reporting success for a file the user never receives is worse
       * than an error, because nobody goes looking for it.
       */
      if (typeof res.csv !== 'string' || res.csv === '') {
        throw new Error('The export came back empty. Nothing was saved.');
      }
      if (!saveCsv(res.csv, res.filename)) {
        throw new Error('This browser would not accept the download. Nothing was saved.');
      }
      setExportState({ busy: false, error: null, done: res });
    } catch (e) {
      setExportState({ busy: false, error: e.message || 'That figure could not be exported.', done: null });
    }
  // ⚠ `filters` and `sort` ARE dependencies. They were missing, so the export
  // carried whatever they were on first render — a stale-closure bug that would
  // have survived the save fix and produced a file quietly disagreeing with the
  // screen.
  }, [metricKey, group, channels, filters, sort]);

  const { loading, error, data } = state;
  const allRows = (data && Array.isArray(data.rows)) ? data.rows : [];

  /** The table's own date column — the first one declared as a date. */
  const dateCol = useMemo(() => COLUMNS.find((c) => c.sortAs === 'date') || null, [COLUMNS]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // ⚠ NO EARLY RETURN ON AN EMPTY SEARCH — P185. It used to `return allRows`
    // here, which short-circuited past the date filter below: the inputs
    // rendered, accepted dates and narrowed nothing whenever the search box
    // happened to be empty, which is almost always.
    // Searches only fields already on screen. Deliberately NOT a scan of the raw
    // row object: a future server-side field would otherwise become searchable —
    // and therefore inferable — without ever being rendered or reviewed.
    const bySearch = !q ? allRows : allRows.filter((r) => COLUMNS.some((c) => {
      const v = r[c.key];
      return v != null && String(v).toLowerCase().includes(q);
    }));
    if (!dateCol || (!dateFrom && !dateTo)) return bySearch;
    /*
     * ⚠ COMPARED AS DATES, never as strings. The cells hold ISO timestamps and
     * the inputs hold YYYY-MM-DD; a string compare would put "2026-9-1" in the
     * wrong place and drop rows with no visible reason.
     * ⛔ `to` is INCLUSIVE, matching the dashboard control — the end of the
     * chosen day, not its start.
     */
    const lo = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const hi = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
    return bySearch.filter((r) => {
      const v = r[dateCol.key];
      if (v == null) return false;         // undated rows cannot satisfy a date window
      const t = new Date(v);
      if (Number.isNaN(t.getTime())) return false;
      if (lo && t < lo) return false;
      if (hi && t > hi) return false;
      return true;
    });
  }, [allRows, query, dateCol, dateFrom, dateTo]);

  const sorted = useMemo(() => {
    // ⚠ No active sort ⇒ the metric's own order, not "whatever COLUMNS[0] is".
    const active = sort.key ? sort : DEFAULT_SORT;
    const col = COLUMNS.find((c) => c.key === active.key) || COLUMNS[0];
    const dir = active.dir === 'asc' ? 1 : -1;
    // Copy before sorting — never mutate the fetched rows in place.
    return [...filtered].sort((a, b) => {
      const av = sortValue(col, a);
      const bv = sortValue(col, b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      // Tie-break on id so the order is deterministic and does not jitter
      // between renders on equal keys.
      return (Number(a.id) - Number(b.id)) * dir;
    });
  }, [filtered, sort]);

  /*
   * ⭐ THREE STATES — asc → desc → OFF, then back to asc. "Off" restores the
   * metric's own declared order (DEFAULT_SORT), which is the order the figure
   * was computed in; without it a reader who sorts a column has no way back to
   * the view the number came from except closing the panel.
   */
  const toggleSort = (key) => setSort((s) => {
    if (s.key !== key) return { key, dir: 'asc' };
    if (s.dir === 'asc') return { key, dir: 'desc' };
    return { ...SORT_OFF };
  });

  /*
   * ⚠ THE WHOLE ROW IS PASSED, not just the contact id. P150's finance lists
   * need the row to open the INVOICE behind it — and because this only ever
   * handed over `contact_id`, that hand-off silently received `undefined` and
   * opened nothing. Live defect, caught by the first test that clicked a
   * finance row.
   *
   * ⚠ The id stays FIRST so every existing caller is unchanged.
   */
  const openContact = (row) => { if (onOpenContact && row.contact_id != null) onOpenContact(row.contact_id, row); };

  // ⚠ STRONG CONTRAST, deliberately. Low-contrast controls are neumorphism's
  // failure mode, and these two controls must be spotted instantly rather than
  // admired: a real border, dark label text, an explicit sort arrow.
  const th = (col) => ({
    textAlign: col.align === 'right' ? 'right' : 'left',
    padding: '9px 12px',
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    // ⚠ Colour stays INLINE and dark — the contrast rule. Background and the
    // 2px sortable underline are owned by App.css's .drilldown-table rules,
    // keyed on aria-sort so the active column is stronger, never softer.
    color: sort.key === col.key ? '#1B5E68' : '#495057',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    /*
     * ⛔ P179 — PINNED while the list scrolls. ⚠ A sticky <th> needs its own
     * opaque background: the default is transparent, so rows scroll THROUGH
     * the header and the result is unreadable rather than merely unpinned.
     */
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: '#F8F9FA',
    userSelect: 'none',
  });

  return (
    <div className="drilldown-panel" style={{ marginTop: 16, padding: 0, overflow: 'hidden' }} data-testid="metric-drilldown">
      <div className="drilldown-panel__header" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand drill-down' : 'Collapse drill-down'}
          aria-expanded={!collapsed}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#2E8B98', display: 'inline-flex', alignItems: 'center', padding: 0 }}
        >
          {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
        </button>
        <strong style={{ fontSize: 14, color: '#212529' }}>{title}</strong>
        {!loading && !error && (
          <span style={{ fontSize: 12, color: '#495057' }}>
            {query.trim()
              ? `${sorted.length} of ${allRows.length} rows`
              : (allRows.length === 1 ? '1 row' : `${allRows.length} rows`)}
          </span>
        )}
        <button onClick={onClose} aria-label="Close drill-down"
          style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: '#6C757D' }}>
          <X size={16} />
        </button>
      </div>

      {!collapsed && (
        <>
          {loading && (
            <div style={{ padding: 24, textAlign: 'center', color: '#6C757D', fontSize: 13 }}>
              <Loader2 size={16} className="spin" /> Opening the rows behind this figure…
            </div>
          )}

          {!loading && error && (
            <div style={{ padding: 20, color: '#991B1B', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldAlert size={16} /> {error}
            </div>
          )}

          {/* Rule 3 — inline on the result, per design §7. */}
          {!loading && !error && data && data.horizonWarning && (
            <div className="drilldown-panel__notice" style={{ padding: '10px 16px', background: '#FEF3C7', color: '#92400E', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={14} /> {data.horizonWarning}
            </div>
          )}

          {/* P140 PR B — which group these rows are, and the way back up. */}
          {group && (
            <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #F1F3F5' }}>
              <button type="button" onClick={onBack} data-testid="drilldown-back"
                style={{ background: 'none', border: '1px solid #CED4DA', borderRadius: 8, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>
                ← Back to summary
              </button>
              <span style={{ fontSize: 12, color: '#495057' }} data-testid="drilldown-group-label">
                {group.label}
              </span>
              {canExport && (
                <button type="button" onClick={doExport} disabled={exportState.busy}
                  data-testid="drilldown-export"
                  style={{ marginLeft: 'auto', background: '#fff', border: '1px solid #1864AB', color: '#1864AB', borderRadius: 8, padding: '3px 11px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  {exportState.busy ? 'Exporting…' : 'Export CSV'}
                </button>
              )}
            </div>
          )}

          {/*
            * ⚠ The export result is REPORTED, including a refusal. A silent
            * failure on the path that takes rows out of the building is the worst
            * shape: the user believes they have the file.
            */}
          {exportState.error && (
            <div data-testid="export-error" style={{ padding: '10px 16px', background: '#FDECEA', color: '#7B241C', fontSize: 12 }}>
              {exportState.error}
            </div>
          )}
          {exportState.done && (
            <div data-testid="export-done" style={{ padding: '10px 16px', background: '#E7F5FF', color: '#1864AB', fontSize: 12 }}>
              Exported {exportState.done.rowCount} row{exportState.done.rowCount === 1 ? '' : 's'} as {exportState.done.filename}.
              {/*
                * ⛔⛔ THE SEARCH IS A CLIENT-SIDE NARROWING AND THE FILE DOES
                * NOT CARRY IT. The export is produced SERVER-SIDE on purpose —
                * that is the path the PHI disclosure audit sits on, and a CSV
                * assembled here from the rows already fetched would take data
                * out of the building with no record of it. So the file holds
                * every row behind the figure, and the screen SAYS SO rather
                * than letting a reader assume the file matches what they are
                * looking at. A file that silently disagrees with the screen is
                * the defect this card is already fixing once.
                */}
              {(query.trim() || dateFrom || dateTo) && (
                <strong data-testid="export-search-note">
                  &nbsp;⚠ Your search and date filter are not applied to the file — it
                  contains all rows behind this figure, not the {sorted.length} shown.
                </strong>
              )}
              {/*
                * A capped file must SAY it is capped. The file outlives the
                * screen that could have explained it.
                */}
              {exportState.done.truncated && (
                <strong data-testid="export-truncated">
                  &nbsp;⚠ This export hit the {exportState.done.maxRows}-row limit and is not complete
                  ({exportState.done.totalRowCount} rows match).
                </strong>
              )}
            </div>
          )}

          {/* Rule 2 — the cap is stated with its number, never implied. */}
          {!loading && !error && data && data.truncated && (
            <div className="drilldown-panel__notice" style={{ padding: '10px 16px', background: '#FEF3C7', color: '#92400E', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={14} />
              Showing the first {data.maxRows} rows. This figure covers more than that, so this list is
              <strong>&nbsp;not complete</strong>.
            </div>
          )}

          {!loading && !error && allRows.length > 0 && (
            <div className="drilldown-panel__searchbar" style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Search size={14} color="#495057" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search these rows…"
                aria-label="Search these rows"
                className="drilldown-panel__search"
                style={{ flex: 1, maxWidth: 320, padding: '7px 10px', fontSize: 13 }}
              />
              {dateCol && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      data-testid="drilldown-date-filter">
                  <span style={{ fontSize: 11.5, color: '#6C757D' }}>{dateCol.label}:</span>
                  <input type="date" value={dateFrom} aria-label={`${dateCol.label} from`}
                         data-testid="drilldown-date-from"
                         onChange={(e) => setDateFrom(e.target.value)}
                         style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #CED4DA', borderRadius: 6 }} />
                  <input type="date" value={dateTo} aria-label={`${dateCol.label} to`}
                         data-testid="drilldown-date-to"
                         onChange={(e) => setDateTo(e.target.value)}
                         style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #CED4DA', borderRadius: 6 }} />
                  {(dateFrom || dateTo) && (
                    <button data-testid="drilldown-date-clear"
                            onClick={() => { setDateFrom(''); setDateTo(''); }}
                            style={{ border: 'none', background: 'transparent', color: '#2E8B98', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                      Clear dates
                    </button>
                  )}
                </span>
              )}
              {query.trim() && (
                <button onClick={() => setQuery('')} style={{ border: 'none', background: 'transparent', color: '#2E8B98', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Rule 1a — nothing behind the figure at all. */}
          {!loading && !error && allRows.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#6C757D', fontSize: 13 }}>
              No rows sit behind this figure.
            </div>
          )}

          {/* Rule 1b — a DIFFERENT fact: rows exist, the search matched none. */}
          {!loading && !error && allRows.length > 0 && sorted.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#6C757D', fontSize: 13 }}>
              No rows match your search. {allRows.length} rows sit behind this figure.
            </div>
          )}

          {!loading && !error && sorted.length > 0 && (
            <div className="responsive-scroll"
                 /* ⛔ P179 item 5 — the LIST scrolls inside the modal, with the
                    header row pinned (see `th`). A 25-row page in a fixed-height
                    modal scrolled the whole dialog, carrying the column names
                    off the top: by row twelve the reader no longer knows which
                    column is Value and which is Days. overscrollBehavior
                    `contain` stops the gesture chaining to the page at the end. */
                 data-testid="drilldown-list-scroll"
                 style={{ maxHeight: '52vh', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
              <table className="drilldown-table" style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        onClick={() => toggleSort(c.key)}
                        onKeyDown={(e) => {
                          // ⚠ A div-shaped control that only responds to a mouse
                          // is unusable by keyboard; <th> is not a button, so the
                          // keys have to be wired explicitly.
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(c.key); }
                        }}
                        tabIndex={0}
                        role="columnheader"
                        title={`Sort by ${c.label}`}
                        style={th(c)}
                        aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      >
                        {/*
                          * ⚠ EVERY sortable column carries an arrow, not only
                          * the active one. A header that sorts but looks inert
                          * is a feature nobody finds — Carlos asked for sorting
                          * on a table that already had it.
                          */}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {c.label}
                          {sort.key === c.key
                            ? (sort.dir === 'asc'
                                ? <ArrowUp size={12} data-testid={`sort-asc-${c.key}`} />
                                : <ArrowDown size={12} data-testid={`sort-desc-${c.key}`} />)
                            : <ChevronsUpDown size={11} aria-hidden="true"
                                data-testid={`sort-idle-${c.key}`}
                                style={{ opacity: 0.35 }} />}
                        </span>
                      </th>
                    ))}
                    <th style={{ padding: '9px 12px' }} />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r, i) => (
                    <tr key={r.id != null ? r.id : i} style={{ borderTop: '1px solid #F1F3F5' }}>
                      {COLUMNS.map((c) => (
                        <td
                          key={c.key}
                          style={{
                            padding: '8px 12px', color: '#212529',
                            textAlign: c.align === 'right' ? 'right' : 'left',
                            fontVariantNumeric: (c.money || c.mono) ? 'tabular-nums' : undefined,
                          }}
                        >
                          {/* The NAME is the primary link — leftmost, where the eye
                              lands. Same drawer hand-off as the trailing button. */}
                          {c.key === 'name' && r.contact_id != null && r.name ? (
                            <button
                              onClick={() => openContact(r)}
                              aria-label={`Open contact ${r.name}`}
                              style={{
                                border: 'none', background: 'transparent', padding: 0,
                                color: '#1B5E68', cursor: 'pointer', fontSize: 12,
                                fontWeight: 700, textDecoration: 'underline',
                              }}
                            >
                              {r.name}
                            </button>
                          ) : cellText(c, r)}
                        </td>
                      ))}
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {r.contact_id != null ? (
                          <button
                            onClick={() => openContact(r)}
                            aria-label={`Open contact for row ${r.id}`}
                            style={{
                              // Real button affordance — a border and a background.
                              // Borderless/transparent read as plain text and was
                              // missed entirely in the first rehearsal.
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '4px 10px', fontSize: 11, fontWeight: 700,
                              color: '#1B5E68', background: '#E7F1F2',
                              border: '1px solid #2E8B98', borderRadius: 6,
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >
                            Open contact <ExternalLink size={11} />
                          </button>
                        ) : (
                          // Honest about an unlinkable row rather than rendering a
                          // dead control that does nothing when pressed.
                          <span style={{ fontSize: 11, color: '#6C757D' }}>no linked contact</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/*
            * P140 PR B — PAGING. `hasMore` is the paging fact and is deliberately
            * NOT the same as `truncated` above: one says there is a next page,
            * the other says the server refused to serve past a hard ceiling.
            * Rendering them as one control would make an ordinary page 2 look
            * like suppressed data.
            */}
          {!loading && !error && data && (data.hasMore || data.offset > 0) && (
            <div data-testid="drilldown-paging"
              style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, borderTop: '1px solid #F1F3F5', fontSize: 12 }}>
              <button type="button" data-testid="page-prev"
                disabled={data.offset <= 0}
                onClick={() => setOffset(Math.max(0, data.offset - data.pageSize))}
                style={{ border: '1px solid #CED4DA', borderRadius: 8, padding: '3px 10px', cursor: data.offset <= 0 ? 'default' : 'pointer', background: '#fff', opacity: data.offset <= 0 ? 0.45 : 1 }}>
                Previous
              </button>
              <span data-testid="page-range" style={{ color: '#495057' }}>
                {data.rowCount === 0
                  ? 'No rows'
                  : `${data.offset + 1}–${data.offset + allRows.length} of ${data.rowCount}`}
              </span>
              <button type="button" data-testid="page-next"
                disabled={!data.hasMore}
                onClick={() => setOffset(data.offset + data.pageSize)}
                style={{ border: '1px solid #CED4DA', borderRadius: 8, padding: '3px 10px', cursor: data.hasMore ? 'pointer' : 'default', background: '#fff', opacity: data.hasMore ? 1 : 0.45 }}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
